import { MDSButton } from "@/mds";
import { useCallback, useState } from "react";
import { useAppStore } from "@/store";
import styled from "@emotion/styled";
import { MDSToggleButton } from "@/mds";
import { observer } from "mobx-react-lite";
import type { InputRequestValue } from "@/components/active-session/types";
import { JsonObject } from "@/domains/common/types";
import {
  AutoOrganizeUpdateCollectionsForm,
  AutoOrganizeCreateCollectionsForm,
} from "@/components/active-session/AutoOrganizeForms";
/**
 * Session events can include INPUT_REQUEST events which include a `form` property.
 * The Session component renders the form for the Input Requests that don't have a corresponding
 * Input Response event. It checks `canRenderForm` and will skip the form if it returns false to avoid
 * any mismatch in the session data with this client's ability to render the form. Otherwise, the Session could
 * get "stuck" for the user.
 *
 * InputForm renders the form component associated with the form type. Each form component should handle its own
 * UI and logic. When the user provides an answer, the form should create an `INPUT_RESPONSE` event in the session
 * that responds to the request at which point the session will close the form.
 * The form may call the `dismissSession` callback to completely dismiss the session, for instance if the user clicks
 * a "cancel" button that should cancel the entire Session.
 */

export const REGISTERED_FORMS = [
  "IMPORT_MARKDOWN",
  "AUTO_ORGANIZE_UPDATE_COLLECTIONS",
  "AUTO_ORGANIZE_CREATE_COLLECTIONS",
] as const;
export type InputFormKind = (typeof REGISTERED_FORMS)[number];

export const canRenderForm = (form?: string) => {
  if (!form) return false;

  return REGISTERED_FORMS.includes(form as InputFormKind);
};

interface InputFormProps {
  inputRequestValue: InputRequestValue;
  dismissSession: () => void;
  metadata: JsonObject;
}

export const InputForm = observer(({ inputRequestValue, dismissSession }: InputFormProps) => {
  switch (inputRequestValue.form) {
    case "IMPORT_MARKDOWN":
      return (
        <ImportMarkdownInitialForm
          requestId={inputRequestValue.id}
          sessionId={inputRequestValue.metadata.session_id as string}
          dismissImportSession={dismissSession}
          metadata={inputRequestValue.metadata}
        />
      );
    case "AUTO_ORGANIZE_UPDATE_COLLECTIONS":
      return (
        <AutoOrganizeUpdateCollectionsForm
          requestId={inputRequestValue.id}
          sessionId={inputRequestValue.metadata.session_id as string}
          dismissAutoOrganizeUpdateCollectionsSession={dismissSession}
          metadata={inputRequestValue.metadata}
        />
      );
    case "AUTO_ORGANIZE_CREATE_COLLECTIONS":
      return (
        <AutoOrganizeCreateCollectionsForm
          requestId={inputRequestValue.id}
          sessionId={inputRequestValue.metadata.session_id as string}
          dismissAutoOrganizeCreateCollectionsSession={dismissSession}
          metadata={inputRequestValue.metadata}
        />
      );

    default:
      console.warn(`Un-implemented input form: ${inputRequestValue.form}`);
      return <div>Coming soon...</div>;
  }
});

interface ImportMarkdownInitialFormProps {
  requestId: string;
  sessionId: string;
  dismissImportSession: () => void;
  metadata: JsonObject;
}

export const ImportMarkdownInitialForm: React.FC<ImportMarkdownInitialFormProps> = observer(
  ({ requestId, sessionId, dismissImportSession, metadata }) => {
    const { store } = useAppStore();
    const [createFromHashtags, setCreateFromHashtags] = useState(true);
    const [useFilenamesAsTitles, setUseFilenamesAsTitles] = useState(false);

    const handleCancel = useCallback(() => {
      // TODO: should we send a cancellation signal to the backend?
      // It will auto-cancel after 12 hours if we don't send one.
      dismissImportSession();
    }, [dismissImportSession]);

    const handleSubmit = useCallback(
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const config = {
          create_collections_from_tags: createFromHashtags,
          use_filenames_as_titles: useFilenamesAsTitles,
        };

        // respond to the input request
        await store.sessionEvents.createInputResponseSessionEvent({
          sessionId,
          config,
          requestId,
          metadata,
          inputKind: "UPLOADED_FILE_BATCH_CONFIG",
        });

        // TODO: this is kinda "faking" it. Instead of sending a message on behalf of Mem from the frontend,
        // we should include the filled out form in the Session and a user-side message.
        const settingsMessage =
          config.create_collections_from_tags && config.use_filenames_as_titles
            ? "Creating collections from hashtags and using filenames as titles."
            : config.create_collections_from_tags
              ? "Creating collections from hashtags."
              : config.use_filenames_as_titles
                ? "Using filenames as titles."
                : "";

        // TODO: Improve the way this message is displayed
        store.sessionEvents.createMessageSentSessionEvent({
          session_id: sessionId,
          message_value: {
            kind: "COMMUNICATION",
            content: `I received your settings. ${settingsMessage}`,
          },
        });
      },
      [
        createFromHashtags,
        useFilenamesAsTitles,
        requestId,
        sessionId,
        store.sessionEvents,
        metadata,
      ]
    );

    return (
      <FormContainer onSubmit={handleSubmit}>
        <FormBody>
          <label htmlFor="create_collections_from_tags">
            Do you want to convert all inline hashtags to collections?
          </label>
          <YesNoInput
            name="create_collections_from_tags"
            value={createFromHashtags}
            onChange={setCreateFromHashtags}
          />
          <label htmlFor="use_filenames_as_titles">Use filenames as titles?</label>
          <YesNoInput
            name="use_filenames_as_titles"
            value={useFilenamesAsTitles}
            onChange={setUseFilenamesAsTitles}
          />
          <ButtonRow>
            <MDSButton onClick={handleCancel}>Cancel</MDSButton>
            <MDSButton color="contrast" type="submit">
              Submit
            </MDSButton>
          </ButtonRow>
        </FormBody>
      </FormContainer>
    );
  }
);

ImportMarkdownInitialForm.displayName = "ImportMarkdownInitialForm";

interface YesNoInputProps {
  name: string;
  value: boolean;
  onChange: (createFromHashtags: boolean) => void;
}
const YesNoInput = ({ name, value, onChange }: YesNoInputProps) => (
  <>
    <input
      type="hidden"
      name={name}
      value={value ? "yes" : "no"}
      onChange={() => onChange(!value)}
    />
    <ToggleOptionsGroup>
      <TintedToggleButton ariaLabel="No" onClick={() => onChange(false)} isSelected={!value}>
        No
      </TintedToggleButton>
      <TintedToggleButton ariaLabel="Yes" onClick={() => onChange(true)} isSelected={value}>
        Yes
      </TintedToggleButton>
    </ToggleOptionsGroup>
  </>
);

const FormContainer = styled.form(({ theme }) => ({
  marginBottom: theme.spacing.md,
  width: "100%",
  padding: `0 ${theme.spacing.lg}`,
}));

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.md} 0;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
  overflow-x: hidden;
  position: relative;
  z-index: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
`;

const ToggleOptionsGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.xs};

  > * {
    flex: 1;
  }
`;

const TintedToggleButton = styled(MDSToggleButton)`
  background: ${({ theme }) => theme.colors.white};
  border-color: ${({ theme }) => theme.colors.grey.x100};
  color: ${({ theme }) => theme.colors.grey.x600};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    border-color: ${({ theme }) => theme.colors.grey.x200};
  }

  &[data-selected] {
    background: ${({ theme }) => theme.colors.mblue.x50};
    border-color: ${({ theme }) => theme.colors.mblue.x100};
    color: ${({ theme }) => theme.colors.mblue.x500};

    &:hover {
      background: ${({ theme }) => theme.colors.mblue.x100};
      border-color: ${({ theme }) => theme.colors.mblue.x200};
    }
  }

  &[data-pressed]:not([data-selected]) {
    background: ${({ theme }) => theme.colors.grey.x25};
    border-color: ${({ theme }) => theme.colors.grey.x200};
    color: ${({ theme }) => theme.colors.grey.x600};
    box-shadow: none;
  }
`;
